import axios from "axios";
import {
  Driver,
  GeoPosition,
  ManifestStop,
  Maybe,
  NestedJobStop,
  RouteOptimizationUnassignedDetail
} from "../generated/graphql";
import {Constants, EmptyValueStrings} from "../constants/constants";
import {isArray} from "lodash";
import {intervalToDuration} from "date-fns";
import {CardTypeV2, StopInformationValue} from "../components/manifest/card-configuration/v2/constants/enums";
import {SortDirection} from "@ag-grid-community/core";
import {JobStopStatus} from "./stops/StopUtils";
import {OverlayToaster, Position} from "@blueprintjs/core";
import {OptionType} from "../components/common/types";
import {CardType, StopLocationValue} from "../components/manifest/card-configuration/common/constants/enums";
import log from "loglevel";

export const arrayDeleteElsePush = (
  arr: any[],
  element: any,
  predicate?: (value: any, index: number, obj: any[]) => unknown
) => {
  const index = predicate
    ? arr.findIndex((value: any, index: number, obj: any[]) => predicate(value, index, obj))
    : arr.indexOf(element);
  if (index > -1) {
    return [...arr.slice(0, index), ...arr.slice(index + 1)];
  } else {
    return [...arr, element];
  }
};

/**
 * returns map equality
 * @param m1
 * @param m2
 */
export function mapCompare<K, V>(m1: Map<K, V>, m2: Map<K, V>): boolean {
  if (m1.size !== m2.size) return false;

  for (const [key, v] of m1) {
    if (!m2.has(key)) return false;

    const v2 = m2.get(key);
    if (!v2) return false;

    if (JSON.stringify(v) !== JSON.stringify(v2)) return false;
  }

  return true;
}

export function groupDetailsByCode(
  detailsArray: RouteOptimizationUnassignedDetail[] | undefined
): RouteOptimizationUnassignedDetail[][] {
  const codeGroups: RouteOptimizationUnassignedDetail[][] = [];
  if (!detailsArray) return [];
  detailsArray.forEach((detail) => {
    const code = detail.code || 0;
    if (!codeGroups[code]) {
      codeGroups[code] = [];
    }

    codeGroups[code].push(detail);
  });

  return codeGroups.filter((group) => group.length > 0);
}

export const capitalizeWords = (str: string) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getArrayFromField = (value: any) => {
  if (!value) {
    return [];
  }
  return isArray(value) ? value : [value];
};

export const getOptionsFromArrayOfObjects = (array: any[], key: string, value: string) => {
  if (!array) return [];
  const options: {key: any; value: any}[] = [];
  array.forEach((item: any) => {
    options.push({key: item[key], value: item[value]});
  });
  return options;
};

export const getKeysFromArrayOfObjects = (array: any[], key: string) => {
  if (!array) return [];
  const keys: any[] = [];
  array.forEach((item: any) => {
    keys.push(item[key]);
  });
  return keys;
};

export const getOptionsFromValues = (values: string[]): OptionType[] => {
  if (!values) return [];
  return values.map((value) => ({key: value, value: value}));
};

export function combineDateAndTime(timeDate: Date, dateDate: Date): Date {
  const hours = timeDate.getHours();
  const minutes = timeDate.getMinutes();
  const seconds = timeDate.getSeconds();
  const year = dateDate.getFullYear();
  const month = dateDate.getMonth();
  const day = dateDate.getDate();
  const combinedDateTime = new Date(year, month, day, hours, minutes, seconds);

  return combinedDateTime;
}

// Cache geocode responses so the same address is never fetched twice.
// Also deduplicates concurrent in-flight requests to the same address.
const geocodeCache = new Map<string, Promise<any>>();

const getGeocodeResponse = (address: string): Promise<any> => {
  if (geocodeCache.has(address)) {
    return geocodeCache.get(address)!;
  }
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${
    Constants.GOOGLE_API_KEY
  }`;
  const promise = axios.get(url).then((r) => r.data);
  geocodeCache.set(address, promise);
  return promise;
};

export const isValidAddress = async (address: string | undefined | null) => {
  if (!address) return false;
  try {
    const data = await getGeocodeResponse(address);
    const isProximate = data.results?.some((result: any) => {
      return result.geometry.location_type === "APPROXIMATE";
    });
    return data.status === "OK" && !isProximate;
  } catch (error) {
    log.error("Error found in address validation:", error);
    return false;
  }
};

export const getAddressByLatLng = async (lat: number, long: number, fn?: (address: string) => void) => {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
    );

    if (fn && response.data.results.length > 0) {
      fn(response.data.results[0].formatted_address);
    }
    return response.data.results[0].formatted_address;
  } catch (error) {
    log.error("Error fetching address:", error);
  }
};

export async function getGeocodedAddress(address: string | undefined): Promise<GeoPosition | undefined> {
  if (!address) return undefined;
  try {
    const data = await getGeocodeResponse(address);
    if (data.status === "OK" && data.results?.length > 0) {
      const location = data.results[0].geometry.location;
      return {latitude: location.lat, longitude: location.lng};
    }
  } catch (error) {
    log.error("Error fetching geocoded address:", error);
  }
  return undefined;
}

export async function getTimeZoneIdByLatLng(lat: number, lng: number): Promise<string | undefined> {
  try {
    const timeZoneResponse = await axios.get(
      `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${Date.now() / 1000}&key=${
        Constants.GOOGLE_API_KEY
      }`
    );

    if (timeZoneResponse.data.status === "OK") {
      return timeZoneResponse.data.timeZoneId;
    } else {
      return undefined;
    }
  } catch (error) {
    log.error("Error getting timezone data.", error);
    return undefined;
  }
}

export async function getTimeZoneIdByAddress(address: string): Promise<string | undefined> {
  try {
    const geocoded = await getGeocodedAddress(address);
    if (geocoded) {
      return await getTimeZoneIdByLatLng(geocoded.latitude, geocoded.longitude);
    }
  } catch (error) {
    log.error("Error getting location data.", error);
  }
  return undefined;
}

export function getTimezoneAbbreviation(timeZone: string, date = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short"
  });

  const parts = formatter.formatToParts(date);
  return parts.find((part) => part.type === "timeZoneName")?.value || undefined;
}

export const isSameDate = (date1: Date | undefined, date2: Date | undefined) => {
  if (!date1 || !date2) return false;
  return date1?.toLocaleDateString() === date2?.toLocaleDateString();
};

export const arrayContainsArray = (superset: any[], subset: any[]) => {
  return subset.every(function (value) {
    return superset.indexOf(value) >= 0;
  });
};

type GetAddressTextParam = {
  stop: ManifestStop | NestedJobStop;
  stopLocationState?: StopInformationValue | StopLocationValue;
  hasDispatchZone?: boolean;
  hasZip?: boolean;
};

export const getStopAddressText = ({stop, stopLocationState, hasDispatchZone, hasZip}: GetAddressTextParam) => {
  const arrayAddress = [];
  switch (stopLocationState) {
    case StopInformationValue.Address: {
      if (stop.address) arrayAddress.push(stop.address);
      break;
    }
    case StopInformationValue.AddressAndZone: {
      if (stop.address) arrayAddress.push(stop.address);
      if (stop.dispatchZone) arrayAddress.push(stop.dispatchZone);
      break;
    }

    case StopLocationValue.CityStateZone:
    case StopInformationValue.CityStateZone: {
      if (stop.city) arrayAddress.push(stop.city);
      if (stop.state) arrayAddress.push(stop.state);
      if (stop.dispatchZone) arrayAddress.push(stop.dispatchZone);
      break;
    }

    case StopLocationValue.ZoneOnly:
    case StopInformationValue.Zone: {
      if (stop.dispatchZone) arrayAddress.push(stop.dispatchZone);
      break;
    }

    default: {
      if (stop.address) arrayAddress.push(stop.address);
      if (stop.city) arrayAddress.push(stop.city);
      if (stop.state) arrayAddress.push(stop.state);
    }
  }
  if (hasZip && stop.zip) {
    arrayAddress.push(stop.zip);
  } else if (hasDispatchZone && stop.dispatchZone) arrayAddress.push(stop.dispatchZone);

  return arrayAddress.join(", ");
};

export const getDriverAddressText = (driver: Driver) => {
  const arrayAddress = [];
  if (driver.address) {
    arrayAddress.push(driver.address);
  }
  if (driver.city) {
    arrayAddress.push(driver.city);
  }
  if (driver.state && driver.state !== "0") {
    arrayAddress.push(driver.state);
  }
  if (driver.zip) {
    arrayAddress.push(driver.zip);
  }
  return arrayAddress.join(", ");
};

export const getStopFullAddressText = (stop: ManifestStop | NestedJobStop) => {
  const arrayAddress = [];
  if (stop.address) arrayAddress.push(stop.address);
  if (stop.city) arrayAddress.push(stop.city);
  if (stop.state) arrayAddress.push(stop.state);
  if (stop.zip) arrayAddress.push(stop.zip);
  return arrayAddress.join(", ");
};

export function findObjectByCondition(array: any[], key: string, value: any) {
  for (const obj of array) {
    if (obj[key] === value) {
      return obj;
    }
  }
  return null;
}

export const isSmallCard = (cardType: CardType | CardTypeV2 | undefined) => {
  if (cardType) {
    return [CardType.Compact, CardType.Minimal, CardTypeV2.Compact, CardTypeV2.Minimal, CardTypeV2.Micro].includes(
      cardType
    );
  }
  return false;
};

export const shouldEllipsize = (text: string | null | undefined, size: number) => {
  if (!text) return false;
  return text.length > size;
};

export const getEllipsizeText = (text: string | undefined | null, size: number) => {
  if (!text) return EmptyValueStrings.unknown;
  return shouldEllipsize(text, size) ? text.substring(0, size - 3) + "..." : text;
};

export const addressSort = (a: string, b: string, direction?: SortDirection) => {
  const houseNumberRegex = /^(\d+)([A-Za-z]*)/;
  const houseNumberA = houseNumberRegex.exec(a) ?? [];
  const houseNumberB = houseNumberRegex.exec(b) ?? [];
  const directionValue = direction === "desc" ? -1 : 1;

  // compare number
  const numA = parseInt(houseNumberA[1]);
  const numB = parseInt(houseNumberB[1]);
  if (numA !== numB) {
    return (numA - numB) * directionValue;
  }
  //compare letter
  const alphaA = houseNumberA[2];
  const alphaB = houseNumberB[2];
  if (alphaA !== alphaB) {
    return alphaA.localeCompare(alphaB) * directionValue;
  }

  const restA = a.slice(houseNumberA[0]?.length);
  const restB = b.slice(houseNumberB[0]?.length);
  return restA.localeCompare(restB) * directionValue;
};

export const isHasUnsupportedStatus = (stops: Maybe<ManifestStop[]> | undefined) => {
  return stops?.some((stop) => {
    return !Object.values(JobStopStatus).includes(stop.jobStopStatus as any);
  });
};

export const millisecondsToDutation = (milliseconds: number | undefined) => {
  if (!milliseconds) return "0h 0m";
  const duration = intervalToDuration({start: 0, end: milliseconds});

  const {days, hours, minutes} = duration;

  return `${milliseconds < 0 ? "-" : ""}${days && days !== 0 ? Math.abs(days) + "d " : ""}${
    hours ? Math.abs(hours) : 0
  }h ${minutes ? Math.abs(minutes) : 0}m`;
};

export const metersToMiles = (meters: number | undefined) => {
  if (!meters) return 0;
  return Number((meters * Constants.MeterToMileRatio).toFixed(2));
};

export const copyToClipboard = async (text: string) => {
  if (!text) return;
  const toastOverLay = OverlayToaster.create({
    maxToasts: 1,
    position: Position.TOP_LEFT
  });
  await navigator.clipboard?.writeText(text).then(
    () => {
      toastOverLay.show({
        message: "Address copied to clipboard",
        icon: "clipboard",
        timeout: 2000,
        intent: "success"
      });
    },
    () => {
      toastOverLay.show({
        message: "Failed to copy address",
        icon: "clipboard",
        timeout: 2000,
        intent: "warning"
      });
    }
  );
};
